import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, formatDistanceToNow, isFuture, isPast } from "date-fns";
import { AlertTriangle, CalendarDays, Clock3, Eye, MapPin, Plus, RefreshCw, Sparkles, Star, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuthSession } from "@/contexts/AuthSessionContext";
import BailBuddiesHeader from "@/components/BailBuddiesHeader";
import SolidSummaryWidget from "@/components/solid/SolidSummaryWidget";
import CommitmentChip from "@/components/CommitmentChip";
import mascot from "@/assets/bailbuddies-mascot.png";
import { getRevealDateTime } from "@/lib/revealTime";

interface Drop {
  id: string;
  title: string;
  description: string | null;
  teaser_message: string | null;
  reveal_date: string;
  reveal_time: string;
  reveal_tz: string | null;
  location_name: string | null;
  plan_type: string;
  is_vault_mode: boolean;
  theme: string;
  creator_id: string;
}

const WATCHDOG_TIMEOUT_MS = 8000;

const planTypeLabels: Record<string, string> = {
  party: "PARTY",
  trip: "TRIP",
  gift: "GIFT",
  proposal: "PROPOSAL",
  surprise: "SURPRISE",
  meetup: "MEETUP",
  announcement: "ANNOUNCEMENT",
  other: "PLAN",
};

const planTypeFills: Record<string, string> = {
  party: "bg-sticker-pink",
  trip: "bg-sticker-mint",
  gift: "bg-sticker-yellow",
  proposal: "bg-sticker-pink",
  surprise: "bg-sticker-purple text-sticker-on-purple",
  meetup: "bg-sticker-mint",
  announcement: "bg-sticker-yellow",
  other: "bg-sticker-surface",
};

const DashboardCohesive = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session, isLoadingAuth } = useAuthSession();
  const mountedRef = useRef(true);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [myDrops, setMyDrops] = useState<Drop[]>([]);
  const [invitedDrops, setInvitedDrops] = useState<Drop[]>([]);
  const [myInviteStatus, setMyInviteStatus] = useState<Record<string, string>>({});
  const [dataLoading, setDataLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [watchdogTriggered, setWatchdogTriggered] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    };
  }, []);

  const fetchDrops = useCallback(async (uid: string) => {
    if (!mountedRef.current) return;
    setDataLoading(true);
    setWatchdogTriggered(false);

    if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    watchdogTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setWatchdogTriggered(true);
    }, WATCHDOG_TIMEOUT_MS);

    try {
      try {
        const profileResult = await supabase.from("profiles").select("full_name").eq("id", uid).maybeSingle();
        if (profileResult.data?.full_name && mountedRef.current) {
          setUserName(profileResult.data.full_name.split(" ")[0]);
        }
      } catch {
        // Profile name is optional; plans should still load.
      }

      const [createdResult, invitedResult] = await Promise.allSettled([
        supabase.from("drops").select("*").eq("creator_id", uid).order("reveal_date", { ascending: true }),
        supabase.from("drop_invites").select("*, drops(*)").eq("user_id", uid),
      ]);

      if (!mountedRef.current) return;

      if (createdResult.status === "fulfilled") {
        const { data, error } = createdResult.value;
        setMyDrops(error ? [] : ((data || []) as Drop[]));
      } else {
        setMyDrops([]);
      }

      if (invitedResult.status === "fulfilled") {
        const { data: invites, error } = invitedResult.value;
        const rows = error ? [] : (invites || []).filter((invite: any) => invite.drops);
        setInvitedDrops(rows.map((invite: any) => invite.drops as Drop));
        setMyInviteStatus(Object.fromEntries(rows.map((invite: any) => [invite.drops.id, invite.status])));
      } else {
        setInvitedDrops([]);
        setMyInviteStatus({});
      }
    } catch (error: any) {
      console.error("Dashboard: fetchDrops failed", error);
      if (mountedRef.current) {
        toast({ title: "Couldn’t load your plans", description: error.message, variant: "destructive" });
      }
    } finally {
      if (mountedRef.current) {
        setDataLoading(false);
        if (watchdogTimerRef.current) {
          clearTimeout(watchdogTimerRef.current);
          watchdogTimerRef.current = null;
        }
      }
    }
  }, [toast]);

  useEffect(() => {
    if (isLoadingAuth) return;
    if (session?.user?.id) fetchDrops(session.user.id);
  }, [fetchDrops, isLoadingAuth, session?.user?.id]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const allDrops = [...myDrops, ...invitedDrops];
  const upcomingDrops = allDrops.filter((drop) => isFuture(getRevealDateTime(drop.reveal_date, drop.reveal_time, drop.reveal_tz)));
  const pastDrops = allDrops.filter((drop) => isPast(getRevealDateTime(drop.reveal_date, drop.reveal_time, drop.reveal_tz)));

  if (isLoadingAuth || !session) return null;

  const revealStatus = (drop: Drop) => {
    const dateTime = getRevealDateTime(drop.reveal_date, drop.reveal_time, drop.reveal_tz);
    if (isPast(dateTime)) return "COMPLETE";
    return formatDistanceToNow(dateTime, { addSuffix: true }).toUpperCase();
  };

  const PlanCard = ({ drop, muted = false }: { drop: Drop; muted?: boolean }) => {
    const inviteStatus = myInviteStatus[drop.id];
    const fill = planTypeFills[drop.plan_type] || planTypeFills.other;

    return (
      <button
        type="button"
        onClick={() => navigate(`/drop/${drop.id}`)}
        className={`sticker-press w-full rounded-[24px] border-4 border-sticker-ink bg-sticker-surface p-4 text-left shadow-sticker-6 ${muted ? "opacity-75" : ""}`}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] border-3 border-sticker-ink shadow-sticker-3 ${fill}`}>
            <span className="font-sticker-display text-[12px] leading-none">{planTypeLabels[drop.plan_type] || "PLAN"}</span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-sticker-display text-[21px] leading-[1.05] text-sticker-ink">{drop.title}</h3>
              {inviteStatus ? (
                <CommitmentChip status={inviteStatus} className="border-sticker-ink" />
              ) : (
                <span className="rounded-full border-2 border-sticker-ink bg-sticker-yellow px-2 py-0.5 text-[11px] font-extrabold text-sticker-ink">HOSTING</span>
              )}
              {drop.is_vault_mode && (
                <span className="rounded-full border-2 border-sticker-ink bg-sticker-surface px-2 py-0.5 text-[11px] font-extrabold text-sticker-ink">SECRET</span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-[13px] font-bold text-sticker-body">
              <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{format(new Date(drop.reveal_date), "MMM d")}</span>
              <span className="flex items-center gap-1.5"><Clock3 className="h-4 w-4" />{format(new Date(`2000-01-01T${drop.reveal_time}`), "h:mm a")}</span>
              {drop.location_name && <span className="flex min-w-0 items-center gap-1.5"><MapPin className="h-4 w-4 shrink-0" /><span className="truncate">{drop.location_name}</span></span>}
            </div>
          </div>

          <span className="self-start rounded-full border-2 border-sticker-ink bg-sticker-page px-3 py-1 text-[10px] font-extrabold tracking-[0.06em] text-sticker-ink">
            {revealStatus(drop)}
          </span>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-sticker-page font-sticker-body text-sticker-ink">
      <BailBuddiesHeader showSettings onSignOut={handleSignOut} />

      <main className="mx-auto max-w-[1080px] px-4 pb-24 pt-8 sm:px-6 md:px-10">
        {watchdogTriggered && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[20px] border-3 border-sticker-ink bg-sticker-yellow p-4 shadow-sticker-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <p className="font-sticker-display text-[17px]">Still loading your plans</p>
                <p className="text-[13px] font-semibold text-sticker-body">Your connection may be taking the scenic route.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => session.user.id && fetchDrops(session.user.id)}
              className="sticker-press flex items-center gap-2 rounded-full border-3 border-sticker-ink bg-sticker-surface px-4 py-2 text-[13px] font-extrabold shadow-sticker-3"
            >
              <RefreshCw className="h-4 w-4" /> Retry
            </button>
          </div>
        )}

        <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
          <div className="grid gap-6 rounded-[32px] border-5 border-sticker-ink bg-sticker-purple p-6 shadow-sticker-12 md:grid-cols-[1fr_auto] md:items-center md:p-8">
            <div>
              <p className="sticker-label text-sticker-on-purple">YOUR BAILBUDDIES</p>
              <h1 className="mt-2 font-sticker-display text-[36px] uppercase leading-[0.92] text-sticker-on-purple md:text-[48px]">
                {userName ? `Hey, ${userName}.` : "You’re in."}
                <br />
                Make the plan. Let it breathe.
              </h1>
              <p className="mt-4 max-w-[680px] text-[16px] font-semibold leading-[1.5] text-sticker-on-purple">
                {dataLoading
                  ? "Pulling your plans together…"
                  : upcomingDrops.length === 0
                    ? "No active plans. Throw one into the group chat and see what happens."
                    : `${upcomingDrops.length} active ${upcomingDrops.length === 1 ? "plan" : "plans"}. Check the vibe, change your mind, or let the plan disappear.`}
              </p>
            </div>
            <img
              src={mascot}
              alt="BailBuddies ghost mascot"
              className="animate-sticker-float h-28 w-28 rounded-[24px] border-4 border-sticker-ink object-cover shadow-sticker-6 md:h-36 md:w-36"
            />
          </div>
        </motion.section>

        <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto_auto]">
          <button
            type="button"
            onClick={() => navigate("/create")}
            className="sticker-press flex min-h-14 items-center justify-center gap-2 rounded-[20px] border-4 border-sticker-ink bg-sticker-pink px-6 font-sticker-display text-[18px] text-sticker-on-pink shadow-sticker-5"
          >
            <Plus className="h-5 w-5" /> Start a plan
          </button>
          <button
            type="button"
            onClick={() => navigate("/contacts")}
            className="sticker-press flex min-h-14 items-center justify-center gap-2 rounded-[20px] border-4 border-sticker-ink bg-sticker-yellow px-5 text-[14px] font-extrabold shadow-sticker-5"
          >
            <Eye className="h-5 w-5" /> Your people
          </button>
          <button
            type="button"
            onClick={() => navigate("/profile#solid")}
            className="sticker-press flex min-h-14 items-center justify-center gap-2 rounded-[20px] border-4 border-sticker-ink bg-sticker-mint px-5 text-[14px] font-extrabold shadow-sticker-5"
          >
            <Star className="h-5 w-5" /> Streaks & badges
          </button>
        </div>

        <div className="mt-6">
          <SolidSummaryWidget />
        </div>

        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="sticker-label text-sticker-purple">PLANS IN PLAY</p>
              <h2 className="mt-1 font-sticker-display text-[28px] uppercase">Your plans</h2>
            </div>
            {upcomingDrops.length > 0 && (
              <span className="rounded-full border-2 border-sticker-ink bg-sticker-surface px-3 py-1 text-[11px] font-extrabold shadow-sticker-3">
                {upcomingDrops.length} ACTIVE
              </span>
            )}
          </div>

          {dataLoading ? (
            <div className="grid gap-3">
              {[0, 1].map((key) => <div key={key} className="h-28 animate-pulse rounded-[24px] border-4 border-sticker-ink bg-sticker-surface" />)}
            </div>
          ) : upcomingDrops.length > 0 ? (
            <div className="grid gap-4">{upcomingDrops.map((drop) => <PlanCard key={drop.id} drop={drop} />)}</div>
          ) : (
            <div className="rounded-[28px] border-4 border-dashed border-sticker-ink bg-sticker-surface p-8 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-sticker-purple" />
              <h3 className="mt-3 font-sticker-display text-[24px]">Nothing on the books</h3>
              <p className="mx-auto mt-2 max-w-[520px] text-[15px] font-semibold text-sticker-body">
                Start a plan, invite your people, and let the group decide its fate.
              </p>
              <button
                type="button"
                onClick={() => navigate("/create")}
                className="sticker-press mt-5 inline-flex items-center gap-2 rounded-full border-3 border-sticker-ink bg-sticker-purple px-5 py-3 font-sticker-display text-[16px] text-sticker-on-purple shadow-sticker-4"
              >
                <Zap className="h-4 w-4" /> Start your first plan
              </button>
            </div>
          )}
        </section>

        {pastDrops.length > 0 && (
          <section className="mt-10">
            <div className="mb-4">
              <p className="sticker-label text-sticker-purple">THE RECEIPTS</p>
              <h2 className="mt-1 font-sticker-display text-[28px] uppercase">Past plans</h2>
            </div>
            <div className="grid gap-3">{pastDrops.slice(0, 5).map((drop) => <PlanCard key={drop.id} drop={drop} muted />)}</div>
          </section>
        )}
      </main>
    </div>
  );
};

export default DashboardCohesive;